import { createSlice } from "@reduxjs/toolkit";
import { logout } from "configs/store-actions";
import { AuthUser, UserResponse } from "apis/user/dtos/responses/user-response";
import { userAuthController } from "apis/user/controllers/user-auth-controller";
import { userController } from "apis/user/controllers/user-controller";
import { AccountType } from "apis/account/enums/account-type";
import { accountSelfController } from "apis/account/controllers/account-self-controller";
import { DateUtil } from "utils/date-util";
import { authenticationPasswordController } from "apis/authentication/controllers/authentication-password-controller";
import { authenticationController } from "apis/authentication/controllers/authentication-controller";
import { AuthenticationResponse } from "apis/authentication/dtos/responses/authentication-response";
import { authenticationUserIdentifierController } from "apis/authentication/controllers/authentication-user-identifier-controller";
import { authenticationOauthController } from "apis/authentication/controllers/authentication-oauth-controller";
import { ApiResponse } from "types/api";
import { userIdentifierController } from "apis/user/controllers/user-identifier-controller";
import { UserIdentifierType } from "apis/user/enums/user-identifier-type";
import { userSettingController } from "apis/user/controllers/user-setting-controller";

type InitialState = {
  authUser: AuthUser;
  isSideNavigation: boolean;
  isIconSidebar: boolean;
  isLoading: boolean;
};

export const initialState: InitialState = {
  authUser: null,
  isSideNavigation: false,
  isIconSidebar: true,
  isLoading: false,
};

export const slice = createSlice({
  name: "global",
  initialState: initialState,
  reducers: {
    setAuthUser: (state, { payload }) => {
      state.authUser = payload;
    },
    toggleSideNavigation: (state, { payload }) => {
      state.isSideNavigation =
        payload !== undefined ? !!payload : !state.isSideNavigation;
    },
    toggleIconSidebar: (state, { payload }) => {
      state.isIconSidebar =
        payload !== undefined ? !!payload : !state.isIconSidebar;
    },
    toggleLoading: (state, { payload }) => {
      state.isLoading = payload !== undefined ? !!payload : !state.isLoading;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(logout, () => ({ ...initialState }))
      .addMatcher(
        authenticationPasswordController.endpoints.signInPasswordAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthController.endpoints.signInOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthController.endpoints.signUpOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthController.endpoints.authenticateOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationUserIdentifierController.endpoints
          .verifyUserIdentifierVerificationAuthentication.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationController.endpoints.refreshAuthentication.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationController.endpoints.switchAccountAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          // A different account: drop the old one's cached details.
          state.authUser = getAuthUserFromAuthenticationResponse(payload);
        }
      )
      .addMatcher(
        authenticationController.endpoints
          .acceptJobInvitationAsFreelancerAuthentication.matchFulfilled,
        (state, { payload }) => {
          // The session moved to a different (new freelancer) account, so
          // drop the old account's cached details rather than merging them.
          state.authUser = getAuthUserFromAuthenticationResponse(payload);
        }
      )
      .addMatcher(
        userAuthController.endpoints.verifyUserAuthEmailAddressVerification
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthController.endpoints.signupUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthController.endpoints.verifyUserAuthEmailAddressVerification
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthController.endpoints.ssoUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthController.endpoints.signinUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userIdentifierController.endpoints.getSelfUserIdentifier.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            userIdentifier: payload.data,
          } as AuthUser;
        }
      )
      .addMatcher(
        userIdentifierController.endpoints.getPrimaryTypeUserIdentifier.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            userEmailAddressIdentifier:
              payload.data.type === UserIdentifierType.EmailAddress
                ? payload.data
                : state.authUser?.userEmailAddressIdentifier,
            userPhoneNumberIdentifier:
              payload.data.type === UserIdentifierType.PhoneNumber
                ? payload.data
                : state.authUser?.userPhoneNumberIdentifier,
          } as AuthUser;
        }
      )
      .addMatcher(
        userController.endpoints.getUserSelf.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            details: payload.data,
            user: payload.data,
          } as AuthUser;
        }
      )
      .addMatcher(
        accountSelfController.endpoints.getAccountSelf.matchFulfilled,
        (state, { payload }) => {
          const accountId = payload.data.id;
          const accountType = payload.data.type;

          state.authUser = {
            ...state.authUser,
            account: payload.data,
            accountClient: payload.data.client,
            accountFreelancer: payload.data.freelancer,
            accountId,
            accountType,
            isClient: accountType == AccountType.Client,
            isFreelancer: accountType == AccountType.Freelancer,
            accountDataNormalized: {},
            accountClientDataNormalized: {},
            accountFreelancerDataNormalized: {},
            // accountDataNormalized: normalizeArray(payload.data?.data || [], {
            //   getKey: ({ key }) => key,
            //   getValue: ({ value }) => value,
            // }),
            // accountClientDataNormalized: normalizeArray(
            //   payload.data?.client?.data || [],
            //   { getKey: ({ key }) => key, getValue: ({ value }) => value }
            // ),
            // accountFreelancerDataNormalized: normalizeArray(
            //   payload.data?.freelancer?.data || [],
            //   { getKey: ({ key }) => key, getValue: ({ value }) => value }
            // ),
          } as AuthUser;
        }
      ),
});

export const { setAuthUser, toggleIconSidebar, toggleLoading } = slice.actions;

export default slice;

export function getStorageState({ authUser }: typeof initialState) {
  return { authUser };
}

function getAuthUserFromAuthenticationResponse(
  payload: ApiResponse<AuthenticationResponse>
) {
  return {
    id: payload.data?.id,
    expiresIn: String(DateUtil.addSeconds(new Date(), 3600)),
    isAuthenticated: !!payload?.access_token,
    accessToken: payload?.access_token,
    accessTokenExpirationDate: payload?.access_token_expiration_date,
    refreshToken: payload?.refresh_token,
    refreshTokenExpirationDate: payload?.refresh_token_expiration_date,
    accountId: payload.data?.account_id,
  } as AuthUser;
}

function getAuthUserFromResponse(payload: ApiResponse<UserResponse>) {
  // const accountId = payload.data.data?.find(
  //   (datum) => datum.key === "default_account_id"
  // )?.value;
  //
  // const accountType = payload.data.data?.find(
  //   (datum) => datum.key === "default_account_type"
  // )?.value;

  return {
    id: payload.data.id,
    details: payload.data,
    expiresIn: String(DateUtil.addSeconds(new Date(), 3600)),
    isAuthenticated: !!payload?.access_token,
    accessToken: payload.access_token,
    // accountId,
    // accountType,
    // isClient: accountType == AccountType.Client,
    // isFreelancer: accountType == AccountType.Freelancer,
  } as AuthUser;
}
